import { TabStyles } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { DataConnectSiteSettingsProps } from '~src/settings/dataconnect-settings/DataConnectSiteSettings';
import { DomainUpdateType, IUpdateBaasEdgePoolResult } from '~src/settings/dataconnect-settings/serverApi/requests/UpdateBaasEdgePoolRequest';
import { BaasEdgePool } from '~src/settings/dataconnect-settings/serverApi/resources/BaasEdgePool';
import { componentRegistryEntry } from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import { cssClass } from '~src/style';

import { DomainID } from '../types/Domain';
import { DomainPermission } from '../types/DomainPermission';
import { DomainTableItem } from '../types/DomainTableItem';
import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { PoolID, SystemDefinedPoolID } from '../types/PoolID';
import { createDomainTableItems, getDomainFromDomainID, getPoolIDForDomainID } from '../utils/DomainUtilities';
import { getGeneralErrorMessage } from '../utils/EdgePoolErrorUtilities';
import { createPoolOptionsFromPools } from '../utils/PoolModelUtilities';
import { AddNewDomainButton } from './AddNewDomainButton';
import { openAddNewDomainDialog } from './addNewDomainDialog/AddNewDomainDialog';
import { openEditDomainDialog } from './addNewDomainDialog/EditDomainDialog';
import { DataConnectNetworkAllowlistInformation } from './DataConnectNetworkAllowlistInformation';
import { openDeleteDomainConfirmationDialog } from './deleteDomainConfirmationDialog/DeleteDomainConfirmationDialog';
import { DomainTable } from './DomainTable';

export const DataConnectNetworkAllowlistSpecs = componentRegistryEntry({
  name: 'DataConnectNetworkAllowlist',
  component: DataConnectNetworkAllowlist
});

export function DataConnectNetworkAllowlist(props: DataConnectSiteSettingsProps) {
  const { refreshBaasEdgePools, hasLoadedBaasEdgePools } = props;
  const poolOptions: ReadonlyArray<PoolAssignmentOption> = React.useMemo(() => createPoolOptionsFromPools(props.baasEdgePools.userDefinedPools), [props.baasEdgePools.userDefinedPools]);
  const domainTableItems: ReadonlyArray<DomainTableItem> = createDomainTableItems(props.baasEdgePools.userDefinedPools, props.baasEdgePools.blockedDomains);

  const onAddNewDomainClicked = React.useCallback(() => {
    const onAddDomainDialogSave = (domainName: string, poolId: PoolID | null, domainPermission: DomainPermission) => {
      const targetPoolId = domainPermission === DomainPermission.ALLOW ? poolId : SystemDefinedPoolID.Blocked;
      BaasEdgePool.updateBaasPool({
        poolId: targetPoolId,
        displayName: null,
        domainChange: { change: DomainUpdateType.Add, domainId: domainName as DomainID },
        siteId: props.baasEdgePools.siteId,
        DataConnectSettingsVersion: '0'
      })
        .then((result: IUpdateBaasEdgePoolResult) => {
          refreshBaasEdgePools();
          if (result.success) {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsAddDomainSuccess() });
          }
          if (result.errors) {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
            });
          }
        });
    };
    openAddNewDomainDialog(onAddDomainDialogSave, poolOptions, domainTableItems.map((item: DomainTableItem) => item.domainName));
  }, [domainTableItems, poolOptions, props.baasEdgePools.siteId, refreshBaasEdgePools]);

  const onEditDomainClicked = React.useCallback((domainId: DomainID) => {
    const onEditDomainDialogSave = (domainName: string, poolId: PoolID | null, domainPermission: DomainPermission) => {
      const targetPoolId = domainPermission === DomainPermission.ALLOW ? poolId : SystemDefinedPoolID.Blocked;
      BaasEdgePool.updateBaasPool({
        poolId: getPoolIDForDomainID(props.baasEdgePools.userDefinedPools, props.baasEdgePools.blockedDomains, domainId),
        displayName: null,
        domainChange: { change: DomainUpdateType.Remove, domainId: domainId as DomainID },
        siteId: props.baasEdgePools.siteId,
        DataConnectSettingsVersion: '0'
      }).then((resultForRemove: IUpdateBaasEdgePoolResult) => {
        if (resultForRemove.errors) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getGeneralErrorMessage(resultForRemove.errors[0].code, resultForRemove.errors[0].message)
          });
        }
        if (resultForRemove.success) {
          BaasEdgePool.updateBaasPool({
            poolId: targetPoolId,
            displayName: null,
            domainChange: { change: DomainUpdateType.Add, domainId: domainName as DomainID },
            siteId: props.baasEdgePools.siteId,
            DataConnectSettingsVersion: '0'
          })
            .then((resultForAdd: IUpdateBaasEdgePoolResult) => {
              refreshBaasEdgePools();
              if (resultForAdd.success) {
                ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsEditDomainSuccess() });
              }
              if (resultForAdd.errors) {
                ToasterService.instance.pop({
                  type: ToastType.Error,
                  text: getGeneralErrorMessage(resultForAdd.errors[0].code, resultForAdd.errors[0].message)
                });
              }
            });
        }
      });
    };
    const domain = getDomainFromDomainID(props.baasEdgePools.userDefinedPools, props.baasEdgePools.blockedDomains, domainId);
    const domainEditableProperties = {
      domainName: domain.domainName,
      poolID: getPoolIDForDomainID(props.baasEdgePools.userDefinedPools, props.baasEdgePools.blockedDomains, domainId),
      domainPermission: domain.domainPermission,
    };
    openEditDomainDialog(domainEditableProperties, onEditDomainDialogSave, poolOptions, domainTableItems.map((item: DomainTableItem) => item.domainName));
  }, [domainTableItems, poolOptions, props.baasEdgePools.siteId, props.baasEdgePools.userDefinedPools, refreshBaasEdgePools, props.baasEdgePools.blockedDomains]);

  const onDeleteDomainClicked = React.useCallback((domainId: string, poolId: PoolID) => {
    const deleteDomainCallback = () => {
      BaasEdgePool.updateBaasPool({
        poolId: poolId,
        displayName: null,
        domainChange: { change: DomainUpdateType.Remove, domainId: domainId as DomainID },
        siteId: props.baasEdgePools.siteId,
        DataConnectSettingsVersion: '0'
      })
        .then((result: IUpdateBaasEdgePoolResult) => {
          refreshBaasEdgePools();
          if (result.success) {
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsDeleteDomainSuccess() });
          }
          if (result.errors) {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
            });
          }
        });
    };
    openDeleteDomainConfirmationDialog(deleteDomainCallback);
  }, [props.baasEdgePools.siteId, refreshBaasEdgePools]);

  if (!FeatureFlags.edgeMultiPoolEnabled) {
    return <></>;
  }
  return (
    <SettingsSection title={Messages.dataConnectSiteSettingsPrivateNetworkAllowlist()} testId={'DataConnect-network-allowlist'}>
      <div className={subHeaderClass}>{Messages.dataConnectSiteSettingsAllowlistRegistry()}</div>
      <DataConnectNetworkAllowlistInformation />
      <AddNewDomainButton onAddNewDomainClicked={onAddNewDomainClicked} hasLoadedEdgePools={hasLoadedBaasEdgePools} />
      <DomainTable
        domainItems={domainTableItems}
        onEditDomainClicked={onEditDomainClicked}
        onDeleteDomainClicked={onDeleteDomainClicked}
        hasLoadedEdgePools={hasLoadedBaasEdgePools}
      />
    </SettingsSection>
  );
}

const subHeaderClass = cssClass('DataConnect-network-allowlist', 'header', {
  fontSize: TabStyles.Typography.Sizing.Default,
  fontWeight: 'bold',
  paddingBottom: TabStyles.Spacing.MediumSpace
});

